import { Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { catchError, tap } from "rxjs/operators";
import { throwError as observableThrowError, Observable } from "rxjs";
import { SmsSettingEntity } from "../../models/entity/sms/sms-setting-entity";
import { SmsRegisterApproveRequest } from "../../models/rest/sms/sms-register-approve-request";
import { SmsRegisterCodeResponse } from "../../models/rest/sms/sms-register-code-response";
import { SendSmsOtpForLoginRequest } from "../../models/rest/sms/send-sms-otp-for-login-request";


import { BaseRestService } from "../base-rest.service";
import { SessionStorageService } from "../session-storage.service";
import { SmsLoginRequest } from '../../models/rest/sms/send-login-request';
import { SendSmsOtpForLoginResponse } from '../../models/rest/sms/send-sms-otp-for-login-response';
import { StoreCacheService } from "../cache/store-cache.service";

@Injectable({
  providedIn: 'root'
})
export class SmsSettingService extends BaseRestService {
  constructor(
    protected httpClient: HttpClient,
    protected sessionStorageService: SessionStorageService,
    private storeCacheService: StoreCacheService
  ) {
    super(httpClient, sessionStorageService);
  }

  getSmsSettings(): Observable<SmsSettingEntity[]> {
    const url = `/sms-settings`;
    return this.httpGet<SmsSettingEntity[]>(url).pipe(catchError(this.errorHandler));
  }

  getSmsSettingById(id) {
    const url = `/sms-settings/${id}`;
    return this.httpGet(url).pipe(catchError(this.errorHandler));
  }

  addSmsSetting(address) {
    const url = `/sms-settings`;
    return this.httpPost(url, address).pipe(
      tap(() => this.storeCacheService.clearAllSmsSettingsCache()),
      catchError(this.errorHandler)
    );
  }

  updateSmsSetting(id, address): Observable<SmsSettingEntity> {
    const url = `/sms-settings/${id}`;
    return this.httpPut<SmsSettingEntity>(url, address).pipe(
      tap(() => this.storeCacheService.clearAllSmsSettingsCache()),
      catchError(this.errorHandler)
    );
  }

  deleteSmsSetting(id) {
    const url = `/sms-settings/${id}`;
    return this.httpDelete(url).pipe(
      tap(() => this.storeCacheService.clearAllSmsSettingsCache()),
      catchError(this.errorHandler)
    );
  }

  getSmsBalance(storeId: number, channelType: string): Observable<number> {
    const url = `/sms-settings/sms-balance?store-id=${storeId}&channel-type=${channelType}`;
    return this.httpGet<number>(url).pipe(catchError(this.errorHandler));
  }


  getSmsOriginators(storeId: number, channelType: string): Observable<string[]> {
    const url = `/sms-settings/originators?store-id=${storeId}&channel-type=${channelType}`;
    return this.httpGet<string[]>(url).pipe(catchError(this.errorHandler));
  }

  getByStoreId(storeId: number, channelType: string) {
    const url = `/sms-settings/by-store-id?store-id=${storeId}&channel-type=${channelType}`;
    return this.httpGet(url).pipe(catchError(this.errorHandler));
  }

  sendOtpForLogin(input: SendSmsOtpForLoginRequest): Observable<SendSmsOtpForLoginResponse> {
    const url = `/sms-settings/send-otp-for-login`;
    return this.httpPost(url, input).pipe(catchError(this.errorHandler));
  }

  login(input: SmsLoginRequest): Observable<SmsSettingEntity> {
    const url = `/sms-settings/login`;
    return this.httpPost<SmsSettingEntity>(url, input).pipe(catchError(this.errorHandler));
  }

  getRegistrationCode(id: number): Observable<SmsRegisterCodeResponse> {
    const url = `/sms-settings/${id}/registration-code`;
    return this.httpPost<SmsRegisterCodeResponse>(url, null).pipe(catchError(this.errorHandler));
  }

  approveRegistration(input: SmsRegisterApproveRequest): Observable<SmsSettingEntity> {
    const url = `/sms-settings/${input.smsSettingId}/approve-registration`;
    return this.httpPost<SmsSettingEntity>(url, input).pipe(catchError(this.errorHandler));
  }

  errorHandler(error: HttpErrorResponse) {
    if (error.message)
      return observableThrowError(error);
    else return observableThrowError(error || " Server Error");
  }
}
